/**
 * drawPlayerAvatar
 * Draws a player on the nightclub canvas using the sprite sheet renderer.
 * Handles: turtle transform, voice glow, emote bubble, chat bubble, name tag.
 *
 * Pure function — pass all dependencies via the `deps` object to keep this
 * decoupled from React internals. The caller is responsible for memoizing.
 */
import { drawPlayerSprite, computeTint, resolveGender } from './playerSprite';
import { DANCE_EMOTES } from './constants';
import { getDanceTransform, getTurtleDance } from './danceAnimations';
import { drawOutfitLayers } from './outfitLayers';
import { drawSpecialSprite, isSheetSpecial } from './specialSprites';

export function drawPlayerAvatar(ctx, x, y, opts, deps) {
  const {
    avatarData,
    isCurrentPlayer = false,
    playerRole = 'user',
    playerName = '',
    isPlayerWalking = false,
    chatMsg = null,
    playerEmote = null,
    isSpeaking = false,
    voiceVolume = 1,
    playerTransform = null,
    playerDirection = 'down',
  } = opts;

  const {
    isMobile,
    isWalking,
    isTurtle,
    currentEmote,
    emoteEndTime,
    walkFrameRef,
    drawTurtle,
    drawChatBubble,
  } = deps;

  // Walking state + walk phase (needed by turtle branch and sprite path)
  const walkingAnimSprite = isPlayerWalking || (isCurrentPlayer && isWalking);
  const walkPhase = isCurrentPlayer
    ? walkFrameRef.current * 0.3
    : Date.now() * 0.012;

  // Determine active emote — used for icon bubble, dance transform, turtle dance
  let activeEmote = null;
  let emoteElapsed = 0;
  if (isCurrentPlayer && currentEmote && Date.now() < emoteEndTime) {
    activeEmote = DANCE_EMOTES.find((e) => e.id === currentEmote);
    if (activeEmote) emoteElapsed = activeEmote.duration - (emoteEndTime - Date.now());
  } else if (playerEmote) {
    activeEmote = playerEmote;
    if (playerEmote.startedAt) {
      emoteElapsed = Date.now() - playerEmote.startedAt;
    } else {
      emoteElapsed = Date.now() % 4000; // synced shared timeline fallback
    }
  }

  // Sheet-special short-circuit (drink transform OR any sheet special outfit)
  const sheetSpecialId = isSheetSpecial(avatarData?.special) ? avatarData.special : null;
  if (playerTransform === 'turtle' || (isCurrentPlayer && isTurtle) || sheetSpecialId) {
    const specId = sheetSpecialId || 'special_turtle_man';
    ctx.save();
    if (activeEmote) {
      // Per-emote turtle choreography (each emote has its own funny animation)
      const td = getTurtleDance(activeEmote.id, emoteElapsed);
      const tcx = x + td.xOffset;
      const tcy = y + td.yOffset - 30;
      ctx.translate(tcx, tcy);
      if (td.rotation) ctx.rotate(td.rotation);
      if (td.scaleX !== 1 || td.scaleY !== 1) ctx.scale(td.scaleX, td.scaleY);
      ctx.translate(-tcx, -tcy);
      drawSpecialSprite(ctx, specId, x + td.xOffset, y + td.yOffset, 1, td.direction, td.isWalking, td.walkPhase);
      ctx.restore();
      // emote icon bubble (was missing for special sprites)
      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(activeEmote.icon, x, y - 100);
      if (activeEmote.id === 'heart') {
        for (let i = 0; i < 3; i++) {
          const ph = (emoteElapsed * 0.05 + i * 24) % 60;
          ctx.globalAlpha = Math.max(0, 1 - ph / 60);
          ctx.font = `${10 + i * 4}px sans-serif`;
          ctx.fillText('❤️', x - 22 + i * 22, y - 74 - ph);
        }
        ctx.globalAlpha = 1;
      }
    } else {
      // gentle breathing idle so he never looks frozen
      if (!walkingAnimSprite) {
        const br = Math.sin(Date.now() * 0.003);
        const bcy = y - 30;
        ctx.translate(x, bcy);
        ctx.scale(1 + br * 0.012, 1 - br * 0.018);
        ctx.translate(-x, -bcy);
      }
      drawSpecialSprite(ctx, specId, x, y, 1, playerDirection || 'down', walkingAnimSprite, walkPhase);
      ctx.restore();
    }

    if (playerName) {
      ctx.font = '10px Inter, sans-serif';
      ctx.textAlign = 'center';
      const nameWidth = ctx.measureText(playerName).width;
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.beginPath();
      ctx.roundRect(x - nameWidth / 2 - 4, y - 60, nameWidth + 8, 16, 4);
      ctx.fill();
      ctx.fillStyle = '#52B788';
      ctx.fillText(playerName, x, y - 48);
    }
    return;
  }

  // Sprite-based rendering below

  // Voice chat speaking indicator
  if (isSpeaking && voiceVolume > 0) {
    ctx.save();
    const glowIntensity = Math.min(1, voiceVolume);
    ctx.shadowColor = `rgba(0, 255, 0, ${glowIntensity})`;
    ctx.shadowBlur = 15 + glowIntensity * 15;
    ctx.beginPath();
    ctx.arc(x, y - 30, 35, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(0, 255, 0, ${0.3 + glowIntensity * 0.5})`;
    ctx.lineWidth = 2 + glowIntensity * 2;
    ctx.stroke();
    ctx.restore();
  }

  const scale = isMobile ? 1.1 : 1.4;
  // Combined hue: user-picked tint + skin-tone offset (works across all sheets)
  const hue = computeTint(avatarData);
  const gender = resolveGender(avatarData?.gender);

  // Idle breathing (suppressed during walk OR dance)
  const breathe = (walkingAnimSprite || activeEmote)
    ? 0
    : Math.sin(Date.now() * 0.0028) * 1.4;

  // Compute dance transform if an emote is active
  const dance = activeEmote ? getDanceTransform(activeEmote.id, emoteElapsed) : null;

  if (dance) {
    // Apply rotation + scale around the sprite center (above feet, mid-body)
    ctx.save();
    const cx = x + dance.xOffset;
    const cy = y + dance.yOffset - (32 * scale); // mid-sprite pivot
    ctx.translate(cx, cy);
    if (dance.rotation) ctx.rotate(dance.rotation);
    if (dance.scaleX !== 1 || dance.scaleY !== 1) ctx.scale(dance.scaleX, dance.scaleY);
    ctx.translate(-cx, -cy);
    drawPlayerSprite(
      ctx,
      x + dance.xOffset,
      y + dance.yOffset,
      dance.direction,
      dance.isWalking,
      dance.walkPhase,
      scale,
      hue,
      gender,
    );
    ctx.restore();
  } else {
    drawPlayerSprite(
      ctx,
      x,
      y + breathe,
      playerDirection || 'down',
      walkingAnimSprite,
      walkPhase,
      scale,
      hue,
      gender,
    );
  }

  // Subtle ground shadow under feet
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.beginPath();
  ctx.ellipse(x, y + 4, 22 * scale * 0.6, 6 * scale * 0.6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Emote bubble icon
  if (activeEmote) {
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(activeEmote.icon, x, y - (isMobile ? 92 : 110));
  }

  // Chat bubble
  if (chatMsg) {
    drawChatBubble(
      ctx,
      x,
      y - (isMobile ? 82 : 100) - (activeEmote ? 25 : 0),
      chatMsg.text,
      playerRole,
      isMobile,
    );
  }

  // Player name tag
  const nameSize = isMobile ? 10 : 12;
  ctx.font = `bold ${nameSize}px sans-serif`;
  ctx.textAlign = 'center';
  if (playerRole === 'owner') {
    ctx.fillStyle = '#DC2626';
    ctx.shadowColor = '#DC2626';
    ctx.shadowBlur = 10;
  } else if (playerRole === 'admin' || playerRole === 'overseer') {
    ctx.fillStyle = '#9333EA';
    ctx.shadowColor = '#9333EA';
    ctx.shadowBlur = 5;
  } else if (playerRole === 'artist') {
    const t = performance.now() * 0.002;
    const h = (Math.sin(t) * 30 + 45) % 360;
    const glow = 6 + Math.sin(t * 1.5) * 4;
    ctx.fillStyle = `hsl(${h}, 100%, 72%)`;
    ctx.shadowColor = `hsl(${h}, 100%, 60%)`;
    ctx.shadowBlur = glow;
  } else {
    ctx.fillStyle = '#FFF';
    ctx.shadowBlur = 0;
  }
  ctx.fillText(playerName || 'Player', x, y + 14);
  ctx.shadowBlur = 0;
}
